// @flow

import type {
  TourBlockout,
  TourBlockoutError,
  TourBlockoutFormField,
  TourBlockoutFormValues,
  TourBlockoutPayload,
} from './types';

type WallClock = {
  year: number,
  month: number, // 0-11, as moment and Date count months
  day: number,
  hour: number,
  minute: number,
};

// The wall-clock time an instant shows in a time zone.
export const wallClockInZone = (ms: number, timezone: string): WallClock => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ms));
  const part = (type: string): number =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    year: part('year'),
    month: part('month') - 1,
    day: part('day'),
    hour: part('hour'),
    minute: part('minute'),
  };
};

const asUtcMs = ({ year, month, day, hour, minute }: WallClock): number =>
  Date.UTC(year, month, day, hour, minute);

// The instant at which `timezone` shows the given wall-clock time. The zone's
// offset is read at a first guess and again at the corrected instant, so a
// time next to a daylight-saving change lands on the right side of it.
export const instantFromWallClock = (
  wallClock: WallClock,
  timezone: string,
): number => {
  const target = asUtcMs(wallClock);
  const offsetAt = (ms: number): number =>
    asUtcMs(wallClockInZone(ms, timezone)) - ms;
  const firstGuess = target - offsetAt(target);
  return target - offsetAt(firstGuess);
};

// The library's date-time picker is dayjs-backed: it hands back a dayjs value,
// which moment can't read (it becomes today at midnight), and it shows nothing
// for a moment. Values cross into and out of the form as native Dates.
const toDate = (value: mixed): ?Date => {
  const date =
    value && typeof (value: Object).toDate === 'function'
      ? (value: Object).toDate()
      : value;
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
};

const wallClockOf = (date: Date): WallClock => ({
  year: date.getFullYear(),
  month: date.getMonth(),
  day: date.getDate(),
  hour: date.getHours(),
  minute: date.getMinutes(),
});

const isSameWallClock = (a: WallClock, b: WallClock): boolean =>
  a.year === b.year &&
  a.month === b.month &&
  a.day === b.day &&
  a.hour === b.hour &&
  a.minute === b.minute;

/**
 * The pickers show and return times in the browser's zone. A blockout belongs
 * to the property, so what the user picks is read as the property's own clock:
 * 12:00 means noon at the property wherever the user is. Without a property
 * zone the browser's is used, and the backend refuses a property that has none.
 */
export const pickerValueToIso = (value: mixed, timezone: ?string): ?string => {
  const picked = toDate(value);
  if (!picked) {
    return null;
  }
  if (!timezone) {
    return picked.toISOString();
  }
  return new Date(
    instantFromWallClock(wallClockOf(picked), timezone),
  ).toISOString();
};

// True when the property's clock never shows the picked time: the hour it
// skips when clocks go forward. No instant matches it, so there is nothing
// truthful to send.
export const isSkippedTime = (value: mixed, timezone: ?string): boolean => {
  const picked = toDate(value);
  if (!picked || !timezone) {
    return false;
  }
  const wallClock = wallClockOf(picked);
  const ms = instantFromWallClock(wallClock, timezone);
  return !isSameWallClock(wallClockInZone(ms, timezone), wallClock);
};

// The reverse of pickerValueToIso: a stored instant as the picker value that
// shows the property's wall-clock time.
export const isoToPickerValue = (iso: ?string, timezone: ?string): ?Date => {
  if (!iso) {
    return null;
  }
  if (!timezone) {
    return new Date(iso);
  }
  const { year, month, day, hour, minute } = wallClockInZone(
    new Date(iso).getTime(),
    timezone,
  );
  return new Date(year, month, day, hour, minute);
};

// A stored instant as text in the property's zone, for the list.
export const formatInZone = (
  iso: string,
  timezone: ?string,
  locale: string,
): string =>
  new Intl.DateTimeFormat(locale, {
    ...(timezone ? { timeZone: timezone } : {}),
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso));

export const toFormValues = (
  tourBlockout: ?TourBlockout,
  timezone: ?string,
): TourBlockoutFormValues => ({
  startTime: isoToPickerValue(tourBlockout?.startTime, timezone),
  endTime: isoToPickerValue(tourBlockout?.endTime, timezone),
  label: tourBlockout?.label ?? '',
  slotsBlocked: tourBlockout?.slotsBlocked ?? '',
});

const showsSameMinute = (value: mixed, iso: ?string, timezone: ?string) => {
  const picked = toDate(value);
  const original = isoToPickerValue(iso, timezone);
  return Boolean(picked && original && picked.getTime() === original.getTime());
};

// A time the user left as it was is sent back exactly as stored. Converting
// the wall-clock time again would move it by an hour if it falls in the hour
// that repeats when clocks go back: 1:30 AM that night happens twice, and the
// picker can't say which. A newly picked time in that hour means the first.
const timeToSend = (
  value: mixed,
  storedIso: ?string,
  timezone: ?string,
): ?string =>
  storedIso && showsSameMinute(value, storedIso, timezone)
    ? storedIso
    : pickerValueToIso(value, timezone);

// Sends what was entered, unchecked: the backend applies every rule. An empty
// number field is sent as null rather than 0, so it is refused as missing.
export const toPayload = (
  values: TourBlockoutFormValues,
  timezone: ?string,
  stored: ?TourBlockout = null,
): TourBlockoutPayload => ({
  startTime: timeToSend(values.startTime, stored?.startTime, timezone),
  endTime: timeToSend(values.endTime, stored?.endTime, timezone),
  label: values.label,
  slotsBlocked:
    values.slotsBlocked === '' || values.slotsBlocked == null
      ? null
      : Number(values.slotsBlocked),
});

const TIME_FIELDS: Array<'startTime' | 'endTime'> = ['startTime', 'endTime'];

// The first time field holding a newly picked time the property's clock
// skips. The form can't turn it into an instant, so it is refused here rather
// than sent as some other time.
export const findSkippedTimeField = (
  values: TourBlockoutFormValues,
  timezone: ?string,
  stored: ?TourBlockout = null,
): ?TourBlockoutFormField =>
  TIME_FIELDS.find(
    (field) =>
      !(stored && showsSameMinute(values[field], stored[field], timezone)) &&
      isSkippedTime(values[field], timezone),
  ) ?? null;

// The form field each backend rule code is about. These codes are part of the
// Tour Blockout endpoint contract (fortress-backend
// src/domain/errors/tourBlockouts.ts). A code not listed here, such as the
// property having no time zone, is shown above the fields instead.
export const FIELD_BY_ERROR_CODE: { [code: string]: TourBlockoutFormField } =
  Object.freeze({
    tour_blockout_start_time_required: 'startTime',
    tour_blockout_end_time_required: 'endTime',
    tour_blockout_end_not_after_start: 'endTime',
    tour_blockout_spans_days: 'endTime',
    tour_blockout_label_required: 'label',
    tour_blockout_label_too_long: 'label',
    tour_blockout_slots_blocked_invalid: 'slotsBlocked',
  });

/**
 * Reads a refusal from the service. With `fullError` the rejection is the
 * backend's error body; a network failure rejects with `{ networkError }`; a
 * plain string is possible from other paths. The backend's message is shown as
 * written, so the form names the rule exactly as the server applied it.
 */
export const readTourBlockoutError = (
  error: mixed,
  fallbackMessage: string,
): TourBlockoutError => {
  if (typeof error === 'string') {
    return { message: error || fallbackMessage, field: null };
  }
  if (error && typeof error === 'object') {
    const { message, networkError, data } = (error: Object);
    const code = data?.code;
    return {
      message:
        (typeof message === 'string' && message) ||
        (typeof networkError === 'string' && networkError) ||
        fallbackMessage,
      field: (typeof code === 'string' && FIELD_BY_ERROR_CODE[code]) || null,
    };
  }
  return { message: fallbackMessage, field: null };
};
